import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UdyogLogo } from "@/components/UdyogLogo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Udyog Classes";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(nextPath);
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen udyog-grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <UdyogLogo size={44} showTagline />
        </div>
        <div className="glass rounded-2xl p-8 border border-neutral-800">
          <h1 className="font-display text-2xl font-bold mb-1">Admin sign in</h1>
          <p className="text-sm text-neutral-400 mb-6">Access your meeting console.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-neutral-400 text-xs uppercase tracking-widest">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 bg-neutral-950 border-neutral-800 h-11 focus-visible:ring-emerald-500"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="text-neutral-400 text-xs uppercase tracking-widest">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 bg-neutral-950 border-neutral-800 h-11 focus-visible:ring-emerald-500"
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
